import { Autocomplete, Box, Button, CircularProgress, debounce, FormControlLabel, IconButton, MenuItem, Radio, Stack, List, ListItem, ListItemAvatar, ListItemText, Paper, SxProps, TextField, Theme, Typography } from "@mui/material"
import { Localized, useLocalization } from "@fluent/react"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { ChangeEvent, useCallback, useState, DragEvent, useRef, ReactNode, useEffect, useMemo } from "react"
import { APIError, AppId } from "../../../app/appTypes"
import { documentExists } from "../documentsApi"
import { If } from "../../../app/If"
import { ImportButton } from "../../../app/components/ImportButton"
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { useNavigate } from "react-router-dom"
import { unwrapResult } from "@reduxjs/toolkit"
import { ServerErrorMsg } from "../../../app/ServerErrorMsg"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import DeleteIcon from "@mui/icons-material/Delete"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import CloseIcon from "@mui/icons-material/Close"
import { LoadingOverlay } from "./LoadingOverlay"
import { StructureTreeSelect } from "../../structure/StructureTreeSelect"
import { CustomDeviceStructureNodeSelect } from "../../structures/components/CustomDeviceStructureNodeSelect"
import { checkStatus, Chunk, loadResult, nextPage, prevPage, resetImport, selectAllChunks, selectMarker, startImport } from "../../ursOffer/markerSlice"
import { LoadingButton } from "@mui/lab"
import { importDocument, ImportCounterpartyModeEnum } from "../importedDocuments/importedDocumentsApi"
import '../importedDocuments/importedDocument.css'
import { extractAiDocumentMeta } from "../../ai/assistantAiApi"
import { Badge } from "@mui/material";
import { uploadBlobs } from "../attachementsSlice"
import { BlobResourceType } from "../blobResourceTypes"
import { CounterpartyListItem, loadCounterparties } from "../../counterparty/counterpartyApi"
import { CountrySelect } from "../../administration/companyProfile/controls/CountrySelect"

const RequiredError = () => <Localized id="field-is-required"><span>Pole jest wymagane</span></Localized>
const DOCUMENT_NUMBER_MAX_LENGTH = 100
type FieldSource = "empty" | "ai" | "user";

type State
    = { tag: 'idle' }
    | { tag: 'waiting' }
    | { tag: 'processing' }
    | { tag: 'finished', success: boolean }

type CompanyMatchResult = {
    publicId: string
    displayName: string
    countryCode: string
    score: number
}

type CounterpartySourceMode = "match" | "existing" | "manual"

export const ImportSupplierUrs = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { l10n } = useLocalization()

    const { step: state } = useAppSelector(selectMarker)
    const chunks = useAppSelector(selectAllChunks)

    const [validationErrors, setValidationErrors] = useState(false)
    const [documentNumber, setDocumentNumber] = useState("");
    const [title, setTitle] = useState("");
    const [structureId, setDeviceTypeId] = useState<AppId>("");
    const [customDeviceStructureNodeId, setCustomDeviceStructureNodeId] = useState<string | null>(null)
    const [relatedCompanyName, setRelatedCompanyName] = useState("");
    const [version, setVersion] = useState<number | null>(null);
    const [engine, setEngine] = useState<string>('marker')
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const formRef = useRef<HTMLFormElement | null>(null)
    const dropboxRef = useRef<HTMLDivElement | null>(null)
    const [ocr, setOcr] = useState(false)
    const [llm, setLlm] = useState(false)
    const [selectedFiles, setSelectedFiles] = useState<FileList | never[] | null>(null)
    const [metadata, setMetadata] = useState<{ key: string; value: string }[]>([]);
    const [error, setError] = useState<APIError | null>(null)
    const [finalizing, setFinalizing] = useState(false)
    const [analyzing, setAnalyzing] = useState(false);
    const [companyMatchResult, setCompanyMatchResult] = useState<CompanyMatchResult[]>([])
    const [counterpartySourceMode, setCounterpartySourceMode] = useState<CounterpartySourceMode>("manual")
    const [selectedMatchedPublicId, setSelectedMatchedPublicId] = useState("")
    const [selectedExistingCounterparty, setSelectedExistingCounterparty] = useState<CounterpartyListItem | null>(null)
    const [manualCountryCode, setManualCountryCode] = useState("")
    const [counterpartyOptions, setCounterpartyOptions] = useState<CounterpartyListItem[]>([])
    const [counterpartiesLoading, setCounterpartiesLoading] = useState(false)
    const [metaAnalyzed, setMetaAnalyzed] = useState(false)

    const initialFieldSource: Record<string, FieldSource> = {
        structureId: "empty",
        documentNumber: "empty",
        title: "empty",
        relatedCompanyName: "empty",
        version: "empty",
    };

    const [fieldSource, setFieldSource] =
        useState<Record<string, FieldSource>>(initialFieldSource);

    const metaRequestedRef = useRef(false);

    const jobIdRef = useRef<number | null>(null)

    const anyFiles: boolean = selectedFiles !== null && selectedFiles.length > 0 ? true : false

    const titleError = validationErrors && title.trim() === "";
    const versionError =
        validationErrors && (version === null || !Number.isFinite(version));

    const sortedCompanyMatches = useMemo(() => {
        return [...companyMatchResult].sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    }, [companyMatchResult])

    const selectedMatchedCompany = useMemo(() => {
        if (!selectedMatchedPublicId) return null
        return sortedCompanyMatches.find((m) => m.publicId === selectedMatchedPublicId) ?? null
    }, [selectedMatchedPublicId, sortedCompanyMatches])

    const selectedCompanyName =
        counterpartySourceMode === "match"
            ? (selectedMatchedCompany?.displayName ?? "")
            : counterpartySourceMode === "existing"
                ? (selectedExistingCounterparty?.displayName ?? "")
                : relatedCompanyName.trim();

    const selectedCountryCode =
        counterpartySourceMode === "match"
            ? (selectedMatchedCompany?.countryCode ?? "")
            : counterpartySourceMode === "existing"
                ? (selectedExistingCounterparty?.countryCode ?? "")
                : manualCountryCode.trim().toUpperCase();

    const relatedCompanyNameError = validationErrors && selectedCompanyName.trim() === "";
    const manualCountryError =
        validationErrors && counterpartySourceMode === "manual" && manualCountryCode.trim() === "";

    useEffect(() => {
        setCounterpartiesLoading(true)
        dispatch(loadCounterparties())
            .then(unwrapResult)
            .then((data) => setCounterpartyOptions(data ?? []))
            .catch((err) => {
                console.error(err)
                setCounterpartyOptions([])
            })
            .finally(() => setCounterpartiesLoading(false))
    }, [dispatch])

    const handleDocumentNumberChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setDocumentNumber(e.target.value);
        markUser("documentNumber");
    };

    const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setTitle(e.target.value);
        markUser("title");
    };
    const handleRelatedCompanyNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setRelatedCompanyName(e.target.value);
        markUser("relatedCompanyName");
    };

    const isEmpty = (v: unknown) =>
        v == null || (typeof v === "string" && v.trim() === "");

    const validateForm = () => {
        const errors = {
            structureId: isEmpty(structureId),
            documentNumber: isEmpty(documentNumber) || documentNumber.trim().length > DOCUMENT_NUMBER_MAX_LENGTH,
            title: isEmpty(title),
            relatedCompanyName: isEmpty(selectedCompanyName),
            countryCode: counterpartySourceMode === "manual" && isEmpty(manualCountryCode),
            version: version == null || !Number.isFinite(version),
            file: !(selectedFiles && selectedFiles.length > 0),
        };

        const hasErrors = Object.values(errors).some(Boolean);

        setValidationErrors(true);
        return { hasErrors, errors };
    };

    const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.stopPropagation()
        e.preventDefault()
    }, [])
    const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.stopPropagation()
        e.preventDefault()
    }, [])
    const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.stopPropagation()
        e.preventDefault()
        const dt = e.dataTransfer
        if (dt) {
            setSelectedFiles(dt.files)
        }
    }, [])
    const handleDeviceTypeIdChange = useCallback((id: AppId) => {
        setDeviceTypeId(id);
        markUser("structureId");
    }, []);
    const poll = useCallback(async () => {
        if (jobIdRef.current) {
            try {
                // console.log('Polling for job status...')
                const st = await dispatch(checkStatus(jobIdRef.current)).unwrap()
                if (st.completed) {
                    const result = await dispatch(loadResult(jobIdRef.current)).unwrap();
                    jobIdRef.current = null
                    // console.log('Job completed')
                    await analyzeDocumentMetaFromResult(result.blocks);
                } else {
                    setTimeout(poll, 5 * 1000)
                }

            } catch (error) {
                console.log('Poll error!', error)
            }
        }
    }, [])

    const handleUploadClick = async () => {
        if (selectedFiles && selectedFiles.length > 0) {
            setMetaAnalyzed(false)
            setCompanyMatchResult([])
            setSelectedMatchedPublicId("")
            let files: File[] = []
            for (const file of selectedFiles) {
                files.push(file)
            }

            try {
                const resp = await dispatch(startImport({
                    file: files[0]
                })).unwrap()
                if (resp) {
                    // if (formRef) {
                    //     formRef.current?.reset()
                    // }
                    // setSelectedFiles(null)
                    console.log(resp)
                    jobIdRef.current = resp.reqId
                    setTimeout(poll, 1 * 1000)
                }

            } catch (error) {
                console.log(error)
                setError(error as APIError)
            }
        }
    }

    const handleFinalizeImportClick = async () => {
        const { hasErrors } = validateForm();
        if (hasErrors) return;
        setValidationErrors(true)
        if (documentNumber && title && structureId) {
            setFinalizing(true)
            try {
                const getCounterpartyMode = () => {
                    if (counterpartySourceMode === "match") return ImportCounterpartyModeEnum.Match;
                    if (counterpartySourceMode === "existing") return ImportCounterpartyModeEnum.Existing;
                    return ImportCounterpartyModeEnum.New;
                };

                const doc = await dispatch(importDocument({
                    number: documentNumber,
                    title,
                    type: 'URS',
                    version: 1,
                    structureId,
                    customDeviceStructureNodeId,
                    language: 'EN',
                    documentKind: 'NORMAL',
                    relatedCompanyName: selectedCompanyName,
                    createDate: new Date(),
                    status: 'N',
                    createdBy: '',
                    createdByFullName: '',
                    counterpartyRequest: {
                        mode: getCounterpartyMode(),
                        publicId: counterpartySourceMode === "match"
                            ? (selectedMatchedCompany?.publicId ?? undefined)
                            : counterpartySourceMode === "existing"
                                ? (selectedExistingCounterparty?.publicId ?? undefined)
                                : undefined,
                        displayName: selectedCompanyName,
                        countryCode: selectedCountryCode,
                    },
                    elements: chunks.map(chunk => {
                        return {
                            id: 0,
                            contentJson: '',
                            elementType: chunk.blockType,
                            order: chunk.order,
                            contentText: chunk.html,
                            pageNumber: chunk.page,
                        }
                    })
                })).unwrap()

                if (selectedFiles && selectedFiles.length > 0) {
                    let files: File[] = []
                    for (const file of selectedFiles) {
                        files.push(file)
                    }
                    await dispatch(uploadBlobs([doc.guid, files, { resourceType: BlobResourceType.Document }]))
                }

                dispatch(resetImport())
                navigate(`/imported-documents/edit/${doc.id}`, { replace: true })
            } catch (error) {
                console.log('Error', error)
            } finally {
                setFinalizing(false)
            }
        }
    }

    const handleClearClick = useCallback(() => {
        setValidationErrors(false);
        setDocumentNumber("");
        setTitle("");
        setDeviceTypeId("");
        setCustomDeviceStructureNodeId(null)
        setRelatedCompanyName("");
        setManualCountryCode("")
        setCompanyMatchResult([])
        setSelectedMatchedPublicId("")
        setSelectedExistingCounterparty(null)
        setCounterpartySourceMode("manual")
        setMetaAnalyzed(false)
        setSelectedFiles(null);
        setError(null);
        setFinalizing(false);
        setVersion(null);
        metaRequestedRef.current = false;
        jobIdRef.current = null;
        setFieldSource(initialFieldSource);
        if (formRef.current) formRef.current.reset();

        dispatch(resetImport());
    }, [dispatch]);

    const handleRemoveFile = (index: number) => {
        setSelectedFiles(null)
        if (formRef) {
            formRef.current?.reset()
        }
    };

    const handleSelectFilesClick = useCallback(() => {
        if (fileInputRef) {
            fileInputRef.current?.click()
        }
    }, [fileInputRef])

    const handleSelectedFilesChange = useCallback(() => {
        if (fileInputRef) {
            setSelectedFiles(fileInputRef.current?.files ?? null)
        }
    }, [fileInputRef, setSelectedFiles])

    const uploading = state === 'ordering' || state === 'waiting'


    const analyzeDocumentMetaFromResult = useCallback(async (blocks: Chunk[]) => {
        if (!blocks || blocks.length === 0) return;

        const htmlPage = blocks
            .filter((c: Chunk) => c.page === 1)
            .sort((a: Chunk, b: Chunk) => a.order - b.order)
            .map((c: Chunk) => c.html)
            .join("\n");

        setAnalyzing(true);
        try {
            const meta = await dispatch(extractAiDocumentMeta({ htmlPage })).unwrap();
            if (meta.structureId != null) { setDeviceTypeId(String(meta.structureId)); markAi("structureId"); }
            if (meta.number) { setDocumentNumber(meta.number); markAi("documentNumber"); }
            if (meta.title) { setTitle(meta.title); markAi("title"); }
            if (meta.issuerCompanyName) { setRelatedCompanyName(meta.issuerCompanyName); markAi("relatedCompanyName"); }
            if (meta.version != null) { setVersion(meta.version); markAi("version"); }
            const matches = ((meta as any).companyMatchResult ?? []) as CompanyMatchResult[]
            const sorted = [...matches].sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
            setCompanyMatchResult(sorted)
            if (sorted.length > 0) {
                setCounterpartySourceMode("match")
                setSelectedMatchedPublicId(sorted[0].publicId)
                setManualCountryCode(sorted[0].countryCode ?? "")
            } else {
                setCounterpartySourceMode("manual")
                setSelectedMatchedPublicId("")
            }
            setMetaAnalyzed(true)
        } finally {
            setAnalyzing(false);
        }
    }, [dispatch]);

    const markAi = (k: keyof typeof fieldSource) =>
        setFieldSource(prev => ({ ...prev, [k]: "ai" }));

    const markUser = (k: keyof typeof fieldSource) =>
        setFieldSource(prev => ({ ...prev, [k]: "user" }));

    const aiBadgeSx = {
        position: "absolute",
        top: 0,
        right: 8,
        zIndex: 2,
        "& .MuiBadge-badge": {
            fontSize: "0.6rem",
            height: 14,
            minWidth: 20,
            borderRadius: 7,
            padding: "0 4px",
        },
    };

    const AiBadge = ({ visible }: { visible: boolean }) => {
        if (!visible) return null;

        return (
            <Badge
                color="secondary"
                badgeContent="AI"
                sx={{
                    "& .MuiBadge-badge": {
                        right: -18,
                        top: 6,
                        padding: "0 6px",
                        fontSize: "0.65rem",
                        height: 16,
                        borderRadius: 8,
                    },
                }}
            />
        );
    };


    return <Box sx={{
        display: 'flex',
        // width: '100%',
        gap: 2,
    }}>
        <Box sx={{
            p: 1,
            flexGrow: 1,
        }}>
            <If condition={state === 'finished'} otherwise={
                <Paper sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    height: 'calc(100vh - 110px)',
                }}>
                    <Typography fontFamily='monospace' color='text.secondary'>
                        <Localized id="document-import-preview-not-available">
                            <span>_Preview not available</span>
                        </Localized>
                    </Typography>
                </Paper>
            }>
                <PreviewSection />
            </If>
        </Box>
        <Paper
            sx={{
                width: 600,
                minWidth: 600,
                maxWidth: 600,
                flex: '0 0 600px',
                p: 2,
                height: 'calc(100vh - 110px)',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    mx: -2,
                    mt: -2,
                    mb: 2,
                    px: 2,
                    py: 1,
                    bgcolor: "grey.100",
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    <Localized id="document-import-parameters-title">
                        <span>_Document parameters</span>
                    </Localized>
                </Typography>
                <IconButton size="small" onClick={() => navigate('/imported-documents')} aria-label="close">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mt: 1, paddingTop: 1, pr: 1 }}>
                <Stack spacing={1}>

                    <ServerErrorMsg err={error} />
                    <Box sx={{ position: "relative" }}>
                        <Badge
                            badgeContent="AI"
                            color="secondary"
                            invisible={fieldSource.structureId !== "ai"}
                            sx={aiBadgeSx}
                        >
                            <Box />
                        </Badge>

                        <StructureTreeSelect
                            key={structureId || "no-structure"}
                            defaultStructureId={structureId}
                            onStructureSelected={handleDeviceTypeIdChange}
                        />
                    </Box>

                    <CustomDeviceStructureNodeSelect
                        value={customDeviceStructureNodeId}
                        onChange={node => setCustomDeviceStructureNodeId(node.id)}
                    />

                    <Box sx={{ position: "relative" }}>
                        <Badge
                            badgeContent="AI"
                            color="secondary"
                            invisible={fieldSource.documentNumber !== "ai"}
                            sx={aiBadgeSx}
                        >
                            <Box />
                        </Badge>

                        <DocNoTextField
                            fullWidth={true}
                            required={true}
                            value={documentNumber}
                            onChange={handleDocumentNumberChange}
                            label={<Localized id="number">Numer</Localized>}
                            showValidation={validationErrors}
                        />
                    </Box>

                    <Box sx={{ position: "relative" }}>
                        {/* AI badge nad Version */}
                        <Badge
                            badgeContent="AI"
                            color="secondary"
                            invisible={fieldSource.version !== "ai"}
                            sx={aiBadgeSx}
                        >
                            <Box />
                        </Badge>
                        <TextField
                            fullWidth
                            required
                            label={<Localized id="document-import-version-number-only"><span>_Version (number only)</span></Localized>}
                            value={Number.isFinite(version) ? version : ""}
                            onChange={(e) => {
                                const raw = e.target.value.trim();
                                if (raw === "") {
                                    setVersion(null);
                                } else if (/^\d+$/.test(raw)) {
                                    setVersion(parseInt(raw, 10));
                                }
                                markUser("version");
                            }}
                            error={versionError}
                            helperText={versionError ? <RequiredError /> : undefined}
                            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        />
                    </Box>

                    <Box sx={{ position: "relative" }}>
                        <Badge
                            badgeContent="AI"
                            color="secondary"
                            invisible={fieldSource.title !== "ai"}
                            sx={aiBadgeSx}
                        >
                            <Box />
                        </Badge>

                        <TextField
                            fullWidth
                            label={<Localized id="title"><span>_Title</span></Localized>}
                            value={title}
                            required
                            onChange={handleTitleChange}
                            error={titleError}
                            helperText={titleError ? <RequiredError /> : undefined}
                        />
                    </Box>


                    <Box sx={{ position: "relative" }}>
                        {/* AI badge nad Document owner */}
                        <Badge
                            badgeContent="AI"
                            color="secondary"
                            invisible={fieldSource.relatedCompanyName !== "ai"}
                            sx={aiBadgeSx}
                        >
                            <Box />
                        </Badge>

                        <Box
                            sx={(theme) => ({
                                p: 1,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1.5,
                            })}
                        >
                            <Stack spacing={1}>
                                <Box
                                    sx={(theme) => ({
                                        mx: 0,
                                        mt: 0,
                                        mb: 0.25,
                                        px: 1,
                                        py: 0,
                                        bgcolor: theme.palette.grey[100],
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                    })}
                                >
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                        <Localized id="imported-documents-counterparty-title">
                                            <span>_Counterparty</span>
                                        </Localized>
                                    </Typography>
                                </Box>

                                {metaAnalyzed && !analyzing && sortedCompanyMatches.length === 0 && (
                                    <Box
                                        sx={(theme) => ({
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.75,
                                            color: theme.palette.warning.main,
                                        })}
                                    >
                                        <ErrorOutlineIcon fontSize="small" />
                                        <Typography variant="caption" sx={{ color: "inherit" }}>
                                            <Localized id="document-import-ai-could-not-find-issuer">
                                                <span>_AI could not find document issuer, please choose from list or add new one.</span>
                                            </Localized>
                                        </Typography>
                                    </Box>
                                )}

                                <Stack direction="row" spacing={2}>
                                    {sortedCompanyMatches.length > 0 && (
                                        <FormControlLabel
                                            control={<Radio size="small" checked={counterpartySourceMode === "match"} onChange={() => setCounterpartySourceMode("match")} />}
                                            label={<Localized id="document-import-ai-match"><span>_AI match</span></Localized>}
                                        />
                                    )}
                                    <FormControlLabel
                                        control={<Radio size="small" checked={counterpartySourceMode === "existing"} onChange={() => setCounterpartySourceMode("existing")} />}
                                        label={<Localized id="document-import-from-counterparties"><span>_From counterparties</span></Localized>}
                                    />
                                    <FormControlLabel
                                        control={<Radio size="small" checked={counterpartySourceMode === "manual"} onChange={() => setCounterpartySourceMode("manual")} />}
                                        label={<Localized id="document-import-manual"><span>_Manual</span></Localized>}
                                    />
                                </Stack>

                                {counterpartySourceMode === "match" && sortedCompanyMatches.length > 0 && (
                                    <TextField
                                        select
                                        fullWidth
                                        required
                                        value={selectedMatchedPublicId}
                                        onChange={(e) => setSelectedMatchedPublicId(e.target.value)}
                                        error={relatedCompanyNameError}
                                        helperText={relatedCompanyNameError ? <RequiredError /> : undefined}
                                        label={
                                            <Localized id="document-import-matched-company">
                                                <span>_Matched company</span>
                                            </Localized>}
                                    >
                                        {sortedCompanyMatches.map((match) => (
                                            <MenuItem key={match.publicId} value={match.publicId}>
                                                {`${match.displayName} (${match.countryCode || "-"}) - ${match.score}%`}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}

                                {counterpartySourceMode === "existing" && (
                                    <Autocomplete
                                        fullWidth
                                        options={counterpartyOptions}
                                        value={selectedExistingCounterparty}
                                        loading={counterpartiesLoading}
                                        onChange={(_, newValue) => setSelectedExistingCounterparty(newValue)}
                                        isOptionEqualToValue={(a, b) => a.publicId === b.publicId}
                                        getOptionLabel={(option) => `${option.displayName} (${option.countryCode || "-"})`}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                error={relatedCompanyNameError}
                                                helperText={relatedCompanyNameError ? <RequiredError /> : undefined}
                                                label={l10n.getString("imported-documents-counterparty-title", null, "Counterparty")}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {counterpartiesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                )}

                                {counterpartySourceMode === "manual" && (
                                    <Stack spacing={1}>
                                        <TextField
                                            fullWidth
                                            required
                                            size="small"

                                            error={relatedCompanyNameError}
                                            helperText={relatedCompanyNameError ? <RequiredError /> : undefined}
                                            value={relatedCompanyName}
                                            onChange={handleRelatedCompanyNameChange}
                                            label={
                                                <Localized id="document-document-company-owner">
                                                    _Document owner
                                                </Localized>
                                            }
                                        />
                                        <CountrySelect
                                            value={manualCountryCode}
                                            onChange={(_, option) => {
                                                if (option?.code) {
                                                    setManualCountryCode(option.code);
                                                }
                                            }}
                                            label={String(l10n.getString("counterparties-country", null, "Country"))}
                                            fullWidth
                                            displayMode="name"
                                            required
                                            size="small"
                                            error={manualCountryError}
                                            helperText={manualCountryError ? <RequiredError /> : undefined}
                                        />
                                    </Stack>
                                )}
                            </Stack>
                        </Box>
                    </Box>
                </Stack>


                <Box sx={{
                    width: "100%",
                    minHeight: 100,
                    border: "2px dashed grey",
                    borderRadius: 8,
                    marginTop: 2,
                    padding: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "background-color 0.2s, border-color 0.2s",
                    "&:hover": {
                        backgroundColor: "#f5f5f5",
                        borderColor: "primary.main",
                    },
                    "&:active": {
                        backgroundColor: "#e0e0e0",
                    },
                }}>
                    <form ref={formRef}>
                        <input
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            type="file"
                            multiple={false}
                            onChange={handleSelectedFilesChange}
                            accept=".pdf, .docx, .xlsx, .xls"
                        />
                    </form>
                    <LoadingOverlay isOpen={uploading || analyzing} text={analyzing ? "Document analysis..." : undefined} />

                    <Box ref={dropboxRef} component="div" onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDrop={handleDrop}>
                        <If condition={!anyFiles}>
                            <UploadFileIcon fontSize="large" color="action" />
                            <Typography variant="body1" color="GrayText" gutterBottom>
                                <Localized id="drag-and-drop-to-upload">
                                    <span>_Drag and drop to upload</span>
                                </Localized>
                            </Typography>
                            <Typography variant="body1" color="GrayText" gutterBottom>
                                <Localized id="or">
                                    <span>_or</span>
                                </Localized>
                            </Typography>
                            <Button size="small" variant="contained" onClick={handleSelectFilesClick}>
                                <Localized id="browse-file">
                                    <span>_Browse file</span>
                                </Localized>
                            </Button>
                        </If>
                        <If condition={anyFiles}>
                            <FileList files={selectedFiles} onRemoveFile={handleRemoveFile} />
                        </If>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                    }}
                >
                    {/* Główne akcje po LEWEJ */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <If condition={state !== "finished"}>
                            <ImportButton loading={uploading} onClick={handleUploadClick} />
                        </If>

                        <If condition={state === "finished"}>
                            <LoadingButton
                                size="small"
                                color="success"
                                variant="contained"
                                loading={finalizing}
                                onClick={handleFinalizeImportClick}
                            >
                                <Localized id="document-import-finalize-import">
                                    <span>_Finalize import</span>
                                </Localized>
                            </LoadingButton>
                        </If>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                        <LoadingButton
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={handleClearClick}
                        >
                            <Localized id="clear">
                                <span>_Clear</span>
                            </Localized>
                        </LoadingButton>
                    </Box>
                </Box>

            </Box>

            <Box sx={{ mt: 1.5, pt: 1, borderTop: (theme) => `1px solid ${theme.palette.divider}`, display: "flex", justifyContent: "center" }}>
                <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={() => navigate('/imported-documents')}
                >
                    <Localized id="close">
                        <span>Close</span>
                    </Localized>
                </Button>
            </Box>

        </Paper >
    </Box >
}

const FileList = ({ files, onRemoveFile }: { files: FileList | never[] | null, onRemoveFile: (index: number) => void }) => {
    if (files === null) {
        return <Localized id="document-import-no-files"><span>_No files</span></Localized>
    }

    let idx: number[] = []
    for (let i = 0; i < files.length; i++) {
        idx.push(i)
    }

    return <List>{idx.map(j => {
        const file = files[j]
        const url = URL.createObjectURL(file)
        return <ListItem>
            <ListItemAvatar>
                <PictureAsPdfIcon />
            </ListItemAvatar>
            <ListItemText primary={file.name} secondary={`size: ${file.size}B`}></ListItemText>
            <IconButton edge="end" aria-label="delete">
                <DeleteIcon color="primary" onClick={() => onRemoveFile(j)} />
            </IconButton>
        </ListItem>
    })}</List>
}

interface Result {
    ok: boolean
    docId: string
}

interface DocNoTextFieldProps {
    value: string;
    onChange: (v: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    sx?: SxProps<Theme>;
    label: ReactNode;
    fullWidth?: boolean;
    required?: boolean;
    showValidation?: boolean;
}
function DocNoTextField(props: DocNoTextFieldProps): JSX.Element {
    const { sx, value, onChange, required, fullWidth, label, showValidation } = props
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const trimmedValue = value.trim()
    const isTooLong = trimmedValue.length > DOCUMENT_NUMBER_MAX_LENGTH

    const [status, setStatus] = useState<undefined | 'checking' | 'ok' | 'exists'>(undefined)

    const check = useCallback(debounce((pattern: string) => {
        setStatus('checking')
        dispatch(documentExists({
            documentNumber: pattern,
            documentType: 'URS',
        }))
            .then(unwrapResult)
            .then(({ exists }) => {
                if (exists) {
                    setStatus('exists')
                } else {
                    setStatus('ok')
                }
            })
            .catch((err) => {
                console.error(err)
                setStatus(undefined)
            })
    }, 800), [])

    useEffect(() => {
        const v = trimmedValue;
        if (!v || isTooLong) return;

        check(v);
    }, [trimmedValue, isTooLong, check]);

    const error: boolean =
        (showValidation && trimmedValue === "") || isTooLong || status === "exists"; let helperText: ReactNode = <></>

    if (!showValidation && trimmedValue === "") {
        helperText = " ";
    } else {
        if (isTooLong) {
            helperText = (
                <Localized
                    id="document-import-document-number-max-length"
                    vars={{ count: DOCUMENT_NUMBER_MAX_LENGTH }}
                >
                    <span>_Maximum length is {DOCUMENT_NUMBER_MAX_LENGTH} characters.</span>
                </Localized>
            );
        } else {
            switch (status) {
                case "checking":
                    helperText = l10n.getString("document-number-checking", null, "Checking document number...");
                    break;
                case "exists":
                    helperText = l10n.getString("error-document-number-exists", null, "Document with this number already exists");
                    break;
                case "ok":
                    helperText = l10n.getString("ok", null, "OK");
                    break;
                default:
                    helperText = trimmedValue === ""
                        ? l10n.getString("document-enter-number-prompt", null, "Please provide document number")
                        : " ";
                    break;
            }
        }
    }

    return <TextField
        value={value}
        onChange={e => {
            onChange(e)
            const nextValue = e.target.value.trim()
            if (nextValue.length <= DOCUMENT_NUMBER_MAX_LENGTH) {
                check(nextValue)
            }
        }}
        error={error}
        helperText={helperText}
        required={required}
        fullWidth={fullWidth}
        label={label}
        inputProps={{ maxLength: DOCUMENT_NUMBER_MAX_LENGTH }}
    />
}

const PreviewSection = () => {
    const dispatch = useAppDispatch()

    const chunks = useAppSelector(selectAllChunks)
    const { current } = useAppSelector(selectMarker)

    const handlePrevClick = useCallback(() => {
        dispatch(prevPage())
    }, [])
    const handleNextClick = useCallback(() => {
        dispatch(nextPage())
    }, [])

    return <div className="imported-document">
        <Paper sx={{
            p: 1,
            height: 'calc(100vh - 110px)',
            overflowY: 'auto',
        }}>
            {chunks.map(chunk => <ChunkBlock key={chunk.id} chunk={chunk} />)}
        </Paper>
    </div>
}

const rg = /<img alt="(.*?)" src="(.*?)"\/>/gm

const ChunkBlock = (props: { chunk: Chunk }) => {
    return <div dangerouslySetInnerHTML={{ __html: props.chunk.html }}></div>
}
